import React, { useState } from 'react';
import useSicReporteGrupos from '../hooks/useSicReporteGrupos.js';
import SicTablaReporteGrupos from './SicTablaReporteGrupos.jsx';
import * as XLSX from 'xlsx';
import '../SicReporteGrupos.css';

const SicReporteGrupos = () => {
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');

    const { generarReporte, reporteData, loading, error } = useSicReporteGrupos();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!fechaDesde || !fechaHasta) {
            alert('Por favor seleccione ambas fechas');
            return;
        }

        const fechaDesdeDate = new Date(fechaDesde);
        const fechaHastaDate = new Date(fechaHasta);
        fechaHastaDate.setHours(23, 59, 59, 999);

        await generarReporte(fechaDesdeDate, fechaHastaDate);
    };

    const handleHoy = () => {
        const hoy = new Date().toISOString().split('T')[0];
        setFechaDesde(hoy);
        setFechaHasta(hoy);
    };

    const handleMesActual = () => {
        const hoy = new Date();
        const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
            .toISOString().split('T')[0];
        const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
            .toISOString().split('T')[0];
        
        setFechaDesde(primerDiaMes);
        setFechaHasta(ultimoDiaMes);
    };

    // Funciones auxiliares para la exportación a Excel
    const obtenerSemanasOrdenadas = (semanasData) => {
        if (!semanasData) return [];
        const semanas = Object.values(semanasData).sort((a, b) => a.año !== b.año ? a.año - b.año : a.mes !== b.mes ? a.mes - b.mes : a.numero - b.numero);
        return semanas.filter(semana => !isNaN(semana.numero));
    };
    const obtenerNumeroSeguro = (valor) => Number(valor) || 0;

    // Función para exportar a Excel - CORREGIDA
    const exportarAExcel = () => {
        if (!reporteData || Object.keys(reporteData).length === 0) {
            alert('No hay datos para exportar. Genere el reporte primero.');
            return;
        }

        try {
            console.log('Iniciando exportación a Excel...', reporteData);
            
            const wb = XLSX.utils.book_new();
            
            Object.entries(reporteData).forEach(([empresa, datosEmpresa], empresaIndex) => {
                console.log(`Procesando empresa: ${empresa}`, datosEmpresa);
                
                if (!datosEmpresa.totales || !datosEmpresa.totales.semanas) {
                    console.warn(`Empresa ${empresa} no tiene datos de semanas`);
                    return;
                }
                
                const semanas = obtenerSemanasOrdenadas(datosEmpresa.totales.semanas);
                const grupos = Object.keys(datosEmpresa.grupos || {}).sort();
                
                const datosExcel = prepararDatosParaExcel(datosEmpresa, semanas, grupos, empresa);
                
                if (datosExcel.length > 0) {
                    const ws = XLSX.utils.aoa_to_sheet(datosExcel);
                    
                    // Ajustar anchos de columnas
                    const colWidths = calcularAnchosColumnas(semanas.length);
                    
                    ws['!cols'] = colWidths;
                    
                    const sheetName = empresa.substring(0, 31) || `Reporte${empresaIndex + 1}`;
                    XLSX.utils.book_append_sheet(wb, ws, sheetName);
                }
            });
            
            // Verificar que el libro tenga hojas
            if (wb.SheetNames.length === 0) {
                alert('No se pudieron generar datos para exportar.');
                return;
            }
            
            const fileName = `Reporte_Compras_${fechaDesde}_a_${fechaHasta}.xlsx`;
            console.log('Guardando archivo:', fileName);
            
            XLSX.writeFile(wb, fileName);
            console.log('Archivo exportado exitosamente');
            
        } catch (error) {
            console.error('Error al exportar a Excel:', error);
            alert('Error al exportar a Excel: ' + error.message);
        }
    };

    const calcularAnchosColumnas = (numSemanas) => {
        const widths = [
            { wch: 35 }, // Producto
            { wch: 15 }, // Código
        ];
        for (let i = 0; i < numSemanas; i++) {
            widths.push({ wch: 12 }); // PU
            widths.push({ wch: 10 }); // Cantidad
            widths.push({ wch: 15 }); // Monto
        }
        widths.push(
            { wch: 12 }, // Cantidad Total
            { wch: 15 }, // Monto Total
            { wch: 15 }  // Costo Promedio
        );
        return widths;
    };

    const obtenerNombreMes = (mes) => {
        const meses = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
        return meses[obtenerNumeroSeguro(mes)] || 'DES';
    };

    // Función para preparar datos - CORREGIDA
    const prepararDatosParaExcel = (datosEmpresa, semanas, grupos, empresa) => {
        const datos = [];
        
        try {
            // 1. Encabezado principal
            datos.push(
                [`Reporte Consolidado de Productos - ${empresa}`],
                [`Período: ${fechaDesde} a ${fechaHasta}`],
                [`Total Grupos: ${grupos.length} | Total Productos: ${Object.keys(datosEmpresa.productos || {}).length}`]
            );
            datos.push([]); // Línea vacía
            
            // 2. Encabezados de columnas
            const encabezados = ['Producto', 'Código'];
            
            semanas.forEach(semana => {
                const nombreMes = obtenerNombreMes(semana.mes);
                encabezados.push(`S${semana.numero} ${nombreMes} PU`);
                encabezados.push(`S${semana.numero} ${nombreMes} Cant`);
                encabezados.push(`S${semana.numero} ${nombreMes} Monto`);
            });
            
            encabezados.push('Cant Total');
            encabezados.push('Monto Total');
            encabezados.push('Costo Prom');
            
            datos.push(encabezados);
            
            // 3. Datos de productos
            grupos.forEach(nombreGrupo => {
                if (!datosEmpresa.grupos?.[nombreGrupo]?.productos) return;
                
                // Encabezado del grupo
                datos.push([`GRUPO: ${nombreGrupo}`]);
                
                // Productos del grupo
                Object.entries(datosEmpresa.grupos[nombreGrupo].productos)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([producto, datosProducto]) => {
                        const fila = [producto, datosProducto.codigoEmpresa || ''];
                        
                        // Datos por semana
                        semanas.forEach(semana => {
                            const claveSemana = `S${semana.numero}_${semana.mes}_${semana.año}`;
                            const datosSemana = datosProducto.semanas?.[claveSemana] || {};
                            
                            const cantidad = obtenerNumeroSeguro(datosSemana.cantidad);
                            const monto = obtenerNumeroSeguro(datosSemana.monto);
                            const precioUnitario = cantidad > 0 && monto > 0 ? monto / cantidad : 0;
                            
                            fila.push(precioUnitario, cantidad, monto);
                        });
                        
                        // Totales del producto
                        const cantTotal = obtenerNumeroSeguro(datosProducto.totales?.cantidadTotal || 0);
                        const montoTotal = obtenerNumeroSeguro(datosProducto.totales?.montoTotal || 0);
                        const costoProm = cantTotal > 0 ? montoTotal / cantTotal : 0;
                        
                        fila.push(cantTotal, montoTotal, costoProm);
                        datos.push(fila);
                    });
                
                // Totales del grupo por semana
                const filaTotalesGrupo = ['TOTAL GRUPO'];
                semanas.forEach(semana => {
                    const claveSemana = `S${semana.numero}_${semana.mes}_${semana.año}`;
                    const { cantidadTotal, montoTotal } = Object.values(datosEmpresa.grupos[nombreGrupo].productos || {}).reduce((acc, prod) => {
                        const datosSemanaProd = prod.semanas?.[claveSemana] || {};
                        acc.cantidadTotal += obtenerNumeroSeguro(datosSemanaProd.cantidad);
                        acc.montoTotal += obtenerNumeroSeguro(datosSemanaProd.monto);
                        return acc;
                    }, { cantidadTotal: 0, montoTotal: 0 });
                    filaTotalesGrupo.push('', cantidadTotal, montoTotal);
                });

                // Totales generales del grupo
                const totalesGrupo = datosEmpresa.grupos[nombreGrupo].totales;
                filaTotalesGrupo.push(
                    totalesGrupo.cantidadTotal,
                    totalesGrupo.montoTotal,
                    totalesGrupo.montoTotal / totalesGrupo.cantidadTotal || 0
                );
                datos.push(filaTotalesGrupo);

                // Línea vacía entre grupos
                datos.push([]);
            });
            
            // Totales por semana consolidados
            const filaTotalesSemanas = ['TOTALES POR SEMANA', ''];
            semanas.forEach(semana => {
                const claveSemana = `S${semana.numero}_${semana.mes}_${semana.año}`;
                const totalSemana = datosEmpresa.totalesPorSemana?.[claveSemana] || { cantidadTotal: 0, montoTotal: 0 };
                filaTotalesSemanas.push('', totalSemana.cantidadTotal, totalSemana.montoTotal);
            });
            datos.push(filaTotalesSemanas);

            // Totales generales
            const { cantidadTotal, montoTotal } = datosEmpresa.totales;
            const costoPromedioGeneral = cantidadTotal > 0 ? montoTotal / cantidadTotal : 0;
            const filaTotalesGenerales = ['TOTAL GENERAL', ...Array(semanas.length * 3 + 1).fill(''), cantidadTotal, montoTotal, costoPromedioGeneral];
            datos.push(filaTotalesGenerales);
            
        } catch (error) {
            console.error('Error al preparar datos para Excel:', error);
            datos.push(['Error al preparar datos: ' + error.message]);
        }
        
        return datos;
    };

    return (
        <div className="container mt-4">
            <h1 className="mb-4">Reporte de Compras - Insumos</h1>
            
            {/* Formulario de fechas */}
            <div className="card mb-4">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="fechas-form-container">
                            <div className="fecha-field">
                                <label htmlFor="fechaDesde" className="form-label">Fecha Desde:</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fechaDesde"
                                    value={fechaDesde}
                                    onChange={(e) => setFechaDesde(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="fecha-field">
                                <label htmlFor="fechaHasta" className="form-label">Fecha Hasta:</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fechaHasta"
                                    value={fechaHasta}
                                    onChange={(e) => setFechaHasta(e.target.value)}
                                    required
                                />
                            </div>
                            
                            <div className="botones-field">
                                <div className="d-flex gap-2 flex-wrap">
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Generando...' : 'Generar Reporte'}
                                    </button>
                                    
                                    <button type="button" className="btn btn-outline-secondary" onClick={handleHoy}>
                                        Hoy
                                    </button>
                                    
                                    <button type="button" className="btn btn-outline-secondary" onClick={handleMesActual}>
                                        Mes Actual
                                    </button>
                                    
                                    {reporteData && Object.keys(reporteData).length > 0 && !loading && (
                                        <button 
                                            type="button" 
                                            className="btn btn-success"
                                            onClick={exportarAExcel}
                                            title="Exportar reporte a Excel"
                                        >                                            
                                            📊 Exportar Excel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
                {error && <div className="alert alert-danger">{error}</div>}
            </div>

            {loading && (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-2">Generando reporte...</p>
                </div>
            )}

            {reporteData && Object.keys(reporteData).length === 0 && !loading && (
                <div className="alert alert-warning">No se encontraron datos para el período seleccionado.</div>
            )}

            {reporteData && Object.keys(reporteData).length > 0 && !loading && (
                <SicTablaReporteGrupos
                    reporteData={reporteData}
                    fechaDesde={fechaDesde}
                    fechaHasta={fechaHasta}
                />
            )}
        </div>
    );
};

export default SicReporteGrupos;